import * as React from 'react';
import styled, { css } from 'styled-components';
import ReactTooltip from 'react-tooltip';
import clsx from 'clsx';
import { SvgImage } from '../SvgImage';
import { getAssetPath } from '../../util/images';
import { NoneButton } from '../AttachmentTag/AttachmentTag';

export interface IActiveCircle {
  right?: boolean;
  left?: boolean;
}

export interface IConnectionDeviceButtonProps {
  onClick: () => void;
  grayed?: boolean;
  id?: string;
  label: string;
  active?: boolean;
  activeCircle: IActiveCircle;
  icon?: string;
  deviceLinked?: boolean;
  disabled?: boolean;
  testId?: string;
  dataTip?: string;
}

export interface DeviceItemButtonProps {
  activeRight?: boolean;
  activeLeft?: boolean;
  grayed?: boolean;
}

const baseActiveCircle = (extraStyle?: string) => css`
  &::after {
    content: '';
    position: absolute;
    width: 5px;
    height: 5px;
    top: 0;
    bottom: 0;
    margin: auto;
    border: 2px solid ${({ theme }) => theme.colors.confPanel};
    border-radius: 5px;
    background-color: ${({ theme }) => theme.colors.confPanel};
    z-index: 9;
    ${extraStyle};
  }
`;

const DeviceItemButton = styled.div<DeviceItemButtonProps>`
  display: flex;
  flex-direction: row;
  padding: 10px;
  align-items: center;
  cursor: pointer;
  position: relative;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.font1};
  ${({ activeRight }) => activeRight && baseActiveCircle('right: -5px')};
  ${({ activeLeft }) => activeLeft && baseActiveCircle('left: -5px')};
  ${({ grayed }) =>
    grayed &&
    `
    opacity: 0.5;
  `};
`;

const ButtonLabel = styled.div`
  padding: 0 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.font3};
`;

const ButtonLinkIcon = styled.div<{ active: boolean; deviceLinked: boolean }>`
  height: 20px;
  width: 20px;
  margin-left: 10px;
  border: 1px solid ${({ theme }) => theme.colors.font3};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ active, theme }) =>
    active &&
    `
    border-color: ${theme.colors.confPanel};
    background-color: ${theme.colors.confPanel};
    svg path {
      fill: ${theme.colors.white};
    }
  `};
  ${({ deviceLinked, theme }) =>
    deviceLinked &&
    `
    background-color: ${theme.colors.confPanel};
    border-color: ${theme.colors.confPanel};
    svg path {
      fill: ${theme.colors.white};
  `};
`;

const ButtonIcon = styled(SvgImage)<{ active: boolean }>`
  padding: 0 5px;
  ${({ active, theme }) =>
    active &&
    `
    svg path {
      fill: ${theme.colors.confPanel};
    }
  `};
`;

const ConnectionDeviceButton = (props: IConnectionDeviceButtonProps) => {
  const {
    onClick,
    grayed,
    id,
    label,
    active,
    activeCircle,
    icon,
    deviceLinked,
    disabled,
    testId,
    dataTip
  } = props;

  const labelEl = React.useRef(undefined);
  const [labelCurrentEl, setLabelCurrentEl] = React.useState<HTMLElement>(null);

  React.useEffect(() => setLabelCurrentEl(labelEl.current), [labelEl.current]);

  function getDataTip(): string | undefined {
    return labelCurrentEl &&
      labelCurrentEl.offsetWidth < labelCurrentEl.scrollWidth
      ? dataTip
      : undefined;
  }

  return (
    <NoneButton
      data-testid={testId || 'connection-device-button'}
      type="button"
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
      disabled={disabled}
      onMouseOver={() => ReactTooltip.rebuild()}
      className={clsx({
        'has-connections': deviceLinked
      })}
    >
      <DeviceItemButton
        activeRight={activeCircle.right}
        activeLeft={activeCircle.left}
        grayed={grayed}
        id={id}
      >
        <ButtonIcon
          active={active}
          inAssetsPath={getAssetPath(`${icon}.svg`)}
        />
        <ButtonLabel data-tip={getDataTip()} ref={labelEl}>
          {label}
        </ButtonLabel>
        <ButtonLinkIcon active={active} deviceLinked={deviceLinked}>
          <SvgImage
            inAssetsPath={getAssetPath('icons/device-action-pair.svg')}
          />
        </ButtonLinkIcon>
      </DeviceItemButton>
    </NoneButton>
  );
};

export default ConnectionDeviceButton;
